import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, History, Loader2, RotateCcw, Save } from "lucide-react";

const fmtSize = (b) => `${(b / 1024 / 1024).toFixed(1)} MB`;
const fmtWhen = (s) => new Date(s).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });

export function SnapshotList({ pw, onError, saveBlob, onRestored }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const headers = { "X-Backup-Password": pw };
  const guard = () => { if (!pw) { toast.error("Şifre girin"); return false; } return true; };
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { onError(e); } finally { setBusy(""); } };

  const load = () => guard() && run("list", async () => setRows((await api.get("/admin/backups", { headers })).data));
  const now = () => guard() && run("now", async () => {
    await api.post("/admin/backups/run", null, { headers, timeout: 300000 });
    toast.success("Yedek alındı"); setRows((await api.get("/admin/backups", { headers })).data);
  });
  const download = (r) => run(`dl-${r.id}`, async () => {
    const res = await api.get(`/admin/backups/${r.id}/download`, { headers, responseType: "blob", timeout: 300000 });
    saveBlob(res.data, r.filename);
  });
  const restore = (r) => {
    if (!window.confirm(`${fmtWhen(r.created_at)} tarihli yedek geri yüklenecek. Bu ortamdaki TÜM veriler değiştirilecek. Devam edilsin mi?`)) return;
    run(`rs-${r.id}`, async () => { onRestored((await api.post(`/admin/backups/${r.id}/restore`, null, { headers, timeout: 600000 })).data); });
  };

  return (
    <div className="mt-6 border-t border-border pt-5" data-testid="snapshot-section">
      <div className="flex flex-wrap items-center gap-3">
        <div className="font-semibold text-sm flex items-center gap-2"><History className="w-4 h-4 text-amber-600" /> Otomatik Yedekler <span className="text-xs text-muted-foreground font-normal">(her gece 03:00, son 7 gün saklanır)</span></div>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={load} disabled={!!busy} data-testid="snapshot-list-button">{busy === "list" && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Yedekleri Göster</Button>
        <Button size="sm" variant="outline" onClick={now} disabled={!!busy} data-testid="snapshot-run-button">{busy === "now" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Şimdi Yedekle</Button>
      </div>
      {rows && (rows.length === 0 ? <div className="text-sm text-muted-foreground mt-3" data-testid="snapshot-empty">Henüz otomatik yedek yok.</div> : (
        <div className="mt-3 divide-y divide-border rounded-lg border border-border" data-testid="snapshot-list">
          {rows.map(r => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`snapshot-${r.id}`}>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.kind === "auto" ? "bg-sky-500/15 text-sky-700 dark:text-sky-400" : "bg-amber-500/15 text-amber-700 dark:text-amber-400"}`}>{r.kind === "auto" ? "OTOMATİK" : "ELLE"}</span>
              <span className="font-medium">{fmtWhen(r.created_at)}</span>
              <span className="text-muted-foreground text-xs">{fmtSize(r.size)}</span>
              <div className="flex-1" />
              <Button size="sm" variant="ghost" onClick={() => download(r)} disabled={!!busy} data-testid={`snapshot-download-${r.id}`}>
                {busy === `dl-${r.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => restore(r)} disabled={!!busy} data-testid={`snapshot-restore-${r.id}`}>
                {busy === `rs-${r.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 text-rose-500" />}
              </Button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
